import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Background from 'assets/images/gameplay/main-stage-bg.png';

import { getImageSrc } from 'helpers/getImageSrc';
import { useGetMainStageData } from 'pages/hooks';

import { useAppSelector } from 'store';

import { ContainedButton } from 'components/Button';
import { Heading4 } from 'components/Typography';
import styled from 'styled-components';

import { TeamView } from './TeamView';
import { getDetailGameForHost } from 'api/game';
import { startGameRound } from 'api/gameplay';

const Container = styled.div`
  min-height: 100%;
  width: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: top center;
`;

const StartRoundButton = styled(ContainedButton)`
  position: fixed;
  bottom: 42px;
  left: 50%;
  transform: translateX(-50%);
`;

export const MainStagePage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const gameState = useAppSelector((state) => state.gameplay.gameState);
  const teamA = useAppSelector((state) => state.gameplay.teamA);
  const teamB = useAppSelector((state) => state.gameplay.teamB);
  const themeBgId = useAppSelector((state) => state.gameplay.game.theme.value);
  const gameId = useAppSelector((state) => state.game.game.id);
  const [isGameInfoLoaded, setIsGameInfoLoaded] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const themeBackgroundUrl = useMemo(() => getImageSrc(themeBgId), [themeBgId]);

  useEffect(() => {
    getDetailGameForHost(
      { eventId: gameId },
      () => {
        setIsGameInfoLoaded(true);
      },
      () => navigate(`/game/${gameId}/panel`),
    );
  }, []);

  const {
    loaded,
    isViewMode,
    isOriginalHost,
    takeControl,
    openTakeControlModal,
    TakeControlModal,
  } = useGetMainStageData(isGameInfoLoaded, roomId);

  const handleStartRound = useCallback(async () => {
    setSubmitLoading(true);

    await startGameRound(
      {
        gameId,
        roomId,
      },
      () => navigate(`/game/gameplay/${roomId}`),
    );

    setSubmitLoading(false);
  }, [gameId, roomId]);

  return (
    <Container
      style={{
        backgroundImage: loaded
          ? `url(${themeBackgroundUrl || Background})`
          : undefined,
      }}
    >
      {!!teamA && (
        <TeamView
          team={teamA}
          playersList={gameState?.teamAPlayersList}
          position='left'
          teamPlay={0}
          isViewMode={isViewMode}
        >
          {!isOriginalHost && loaded && (
            <ContainedButton
              size='md'
              containedColor='danger'
              onClick={() => navigate(`/game/${gameId}/panel`)}
              style={{ position: 'absolute', right: -254, top: 10 }}
            >
              Exit
            </ContainedButton>
          )}
        </TeamView>
      )}
      {!!teamB && (
        <TeamView
          team={teamB}
          playersList={gameState?.teamBPlayersList}
          position='right'
          teamPlay={1}
          isViewMode={isViewMode}
        >
          {isViewMode && loaded && (
            <ContainedButton
              size='md'
              onClick={openTakeControlModal}
              style={{ position: 'absolute', left: -254, top: 10 }}
            >
              Take Control
            </ContainedButton>
          )}
        </TeamView>
      )}

      <StartRoundButton
        containedColor='success'
        size='md'
        onClick={handleStartRound}
        disabled={isViewMode || !loaded || submitLoading}
      >
        Start Round 1
      </StartRoundButton>

      <TakeControlModal
        header={'TAKE CONTROL?'}
        submitAction={{
          title: 'YES',
          onSubmit: takeControl,
        }}
        submitOnEnter
      >
        <div style={{ padding: '20px 0' }}>
          <Heading4 style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
            Are you sure to take control
            <br />
            in this matchup?
          </Heading4>
        </div>
      </TakeControlModal>
    </Container>
  );
};
